import { FaCircleUser, FaSnowflake, FaBrain, FaBell } from "react-icons/fa6";
import { Link } from "react-router-dom";
import { useTelegram } from "../../context/TelegramContext";
import { getTopMenuData } from "../../api/mainApi";
import { useEffect, useState, useRef } from "react";
import { GiFrogPrince } from "react-icons/gi";
import News from "../news/News";
import { useTranslation } from "react-i18next";

function TopMenu() {
  const [freezing, setFreezing] = useState(null);
  const [points, setPoints] = useState(null);
  
  // Ініціалізація преміуму з localStorage, щоб уникнути блимання кнопки
  const [isPremium, setIsPremium] = useState(() => {
    const savedPremium = localStorage.getItem("premium");
    return savedPremium ? Number(savedPremium) > 0 : false;
  });
  
  const tg = useTelegram();
  const { t } = useTranslation();
  const [hasUnviewed, setHasUnviewed] = useState(false);
  const [isNewsOpen, setIsNewsOpen] = useState(false);

  const newsRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const fetchTopMenuData = async () => {
      try {
        const data = await getTopMenuData();
        setFreezing(data.freezing);
        setPoints(data.points);
        setIsPremium(data.premium > 0);
        localStorage.setItem("premium", data.premium);
      } catch (error) {
        console.error("Помилка отримання TopMenuData:", error);
      }
    };

    fetchTopMenuData();
  }, []);

  useEffect(() => {
    if (!isNewsOpen) return;

    const handleInteraction = (event) => {
      if (
        newsRef.current?.contains(event.target) ||
        buttonRef.current?.contains(event.target)
      ) {
        return;
      }
      setIsNewsOpen(false);
    };

    const handleScroll = () => {
      setIsNewsOpen(false);
    };

    document.addEventListener("mousedown", handleInteraction);
    document.addEventListener("touchstart", handleInteraction);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("mousedown", handleInteraction);
      document.removeEventListener("touchstart", handleInteraction);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isNewsOpen]);

  return (
    <div className="-mx-4 px-4 pt-11 pb-2 bg-gray-950 relative">
      <style>{`
        /* Анімація тільки через opacity, щоб не блокувати position: fixed у News */
        @keyframes fadeOnly {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        .animate-news-popup {
          animation: fadeOnly 0.2s ease-out forwards;
        }
      `}</style>

      {/* Верхній рядок — лічильники (фіксована висота h-7 для уникнення стрибків) */}
      <div className="flex justify-center gap-8 mb-4 h-7 items-center">
        <div className="flex items-center gap-2 h-full">
          <FaSnowflake className="w-5 h-5 text-white" />
          {freezing != null ? (
            <span className="text-xl font-bold leading-none">{freezing}</span>
          ) : (
            <div className="h-6 w-8 animate-pulse bg-white/20 rounded-md" />
          )}
        </div>

        <div className="flex items-center gap-2 h-full">
          <FaBrain className="w-5 h-5 text-white" />
          {points != null ? (
            <span className="text-xl font-bold leading-none">{points}</span>
          ) : (
            <div className="h-6 w-14 animate-pulse bg-white/20 rounded-md" />
          )}
        </div>
      </div>

      {/* Нижній рядок — МІНІАТЮРНІ ЕЛЕГАНТНІ КНОПКИ */}
      <div className="grid grid-cols-3 gap-2">
        
        {/* Кнопка Premium */}
        <Link
          to="/premium"
          className={`flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-2xl transition-all duration-300 shadow-sm overflow-hidden ${
            isPremium
              ? "bg-gradient-to-tr from-emerald-500 via-cyan-500 to-emerald-500 bg-gradient-size animate-gradient-move text-white shadow-emerald-500/20"
              : "bg-white/[0.05] border border-amber-500/30 hover:bg-amber-500/10 text-amber-400"
          }`}
        >
          <div className="relative flex items-center justify-center shrink-0">
            {!isPremium && (
              <div className="absolute w-3.5 h-3.5 rounded-full bg-amber-500/40 blur-[6px] animate-pulse" />
            )}
            <GiFrogPrince className={`w-4 h-4 relative drop-shadow-md ${isPremium ? "text-white" : ""}`} />
          </div>
          <span className={`text-[13px] font-medium tracking-wide ${isPremium ? "text-white" : ""}`}>
            Premium
          </span>
        </Link>

        {/* Кнопка Новини */}
        <button
          ref={buttonRef}
          onClick={() => setIsNewsOpen((prev) => !prev)}
          className={`flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-2xl transition-all duration-300 shadow-sm overflow-hidden ${
            isNewsOpen
              ? "bg-white/15 border border-white/20"
              : "bg-white/[0.05] border border-white/[0.08] hover:bg-white/10 hover:border-white/15"
          }`}
        >
          <div className="relative flex items-center shrink-0">
            <FaBell
              className={`w-3.5 h-3.5 transition-colors ${hasUnviewed ? "text-cyan-400" : "text-white/70"}`}
            />
            {hasUnviewed && (
              <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-cyan-400 border border-gray-950 animate-pulse" />
            )}
          </div>
          <span className="text-[13px] font-medium text-white/90 tracking-wide">
            {t("news")}
          </span>
        </button>

        {/* Кнопка Профіль */}
        <Link
          to="/profile"
          className="flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-2xl bg-white/[0.05] border border-white/[0.08] hover:bg-white/10 hover:border-white/15 transition-all duration-300 shadow-sm overflow-hidden"
        >
          {tg.initDataUnsafe?.user?.photo_url ? (
            <img
              src={tg.initDataUnsafe?.user?.photo_url}
              className="w-4 h-4 rounded-full object-cover border border-white/20 shrink-0"
              alt="Avatar"
            />
          ) : (
            <FaCircleUser className="w-4 h-4 text-white/70 shrink-0" />
          )}
          <span className="text-[13px] font-medium text-white/90 truncate max-w-[70px] tracking-wide">
            {tg.initDataUnsafe?.user?.first_name || "User"}
          </span>
        </Link>
        
      </div>

      {/* Попап новин */}
      {isNewsOpen && (
        <div
          ref={newsRef}
          className="absolute top-[136px] inset-x-4 max-h-[70vh] overflow-y-auto bg-gray-900 border border-white/10 rounded-2xl shadow-2xl z-50 p-4 animate-news-popup lg:custom-scrollbar"
        >
          <News
            onUnviewedStatus={setHasUnviewed}
            onClose={() => setIsNewsOpen(false)}
          />
        </div>
      )}
    </div>
  );
}

export default TopMenu;